export type PageLifecycleEntry = {
  event: 'bootstrap' | 'visibilitychange' | 'pageshow' | 'pagehide';
  navigationType: string;
  visibilityState: DocumentVisibilityState;
  wasDiscarded: boolean;
  persisted?: boolean;
};

declare global {
  interface Document {
    wasDiscarded?: boolean;
  }

  interface Window {
    __pageLifecycleEntries?: PageLifecycleEntry[];
    __pageLifecycleProbeStarted?: boolean;
  }
}

const MAX_ENTRIES = 20;

const isEnabled = () => process.env.NODE_ENV === 'development';

const getNavigationType = () => {
  const navigation = performance.getEntriesByType(
    'navigation',
  )[0] as PerformanceNavigationTiming | undefined;

  return navigation?.type || 'unknown';
};

const record = (entry: PageLifecycleEntry) => {
  const entries = window.__pageLifecycleEntries || [];

  entries.push(entry);
  if (entries.length > MAX_ENTRIES) {
    entries.splice(0, entries.length - MAX_ENTRIES);
  }
  window.__pageLifecycleEntries = entries;
  console.warn('[page-lifecycle]', JSON.stringify(entry));
};

export const startPageLifecycleProbe = () => {
  if (!isEnabled() || window.__pageLifecycleProbeStarted) {
    return undefined;
  }

  window.__pageLifecycleProbeStarted = true;
  const createEntry = (
    event: PageLifecycleEntry['event'],
    persisted?: boolean,
  ): PageLifecycleEntry => ({
    event,
    navigationType: getNavigationType(),
    visibilityState: document.visibilityState,
    wasDiscarded: Boolean(document.wasDiscarded),
    persisted,
  });
  const handleVisibilityChange = () => record(createEntry('visibilitychange'));
  const handlePageShow = (event: PageTransitionEvent) =>
    record(createEntry('pageshow', event.persisted));
  const handlePageHide = (event: PageTransitionEvent) =>
    record(createEntry('pagehide', event.persisted));

  record(createEntry('bootstrap'));
  document.addEventListener('visibilitychange', handleVisibilityChange);
  window.addEventListener('pageshow', handlePageShow);
  window.addEventListener('pagehide', handlePageHide);

  return () => {
    document.removeEventListener('visibilitychange', handleVisibilityChange);
    window.removeEventListener('pageshow', handlePageShow);
    window.removeEventListener('pagehide', handlePageHide);
    delete window.__pageLifecycleProbeStarted;
  };
};
